import { memo, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { AlertTriangle, Bell, Check, Info, X } from 'lucide-react'
import { useToasts, useStore, useUnreadNoticeCount } from '../../store/index.js'
import type { Toast, ToastAction } from '../../store/slices/toastSlice.js'
import './FeedbackOverlays.css'

const ICONS: Record<Toast['severity'], typeof Check> = {
  success: Check,
  error:   X,
  warning: AlertTriangle,
  info:    Info,
}

const ToastActionButton = memo(function ToastActionButton({
  action, toastId,
}: { action: ToastAction; toastId: string }) {
  const dismiss = useStore(state => state.dismissToast)
  const [running, setRunning] = useState(false)

  const handleAction = useCallback(async () => {
    if (running) return
    setRunning(true)
    try {
      await action.onClick()
      if (!action.keepOpen) dismiss(toastId)
    } finally {
      setRunning(false)
    }
  }, [action, dismiss, running, toastId])

  return (
    <button type="button" onClick={handleAction} disabled={running} className="tp-toast__action">
      {running ? '…' : action.label}
    </button>
  )
})

/**
 * Une carte de la pile. Le minuteur se met en pause au survol et au focus
 * clavier — on ne retire pas un texte que l'opérateur est en train de lire —
 * et repart de zéro à chaque regroupement (`bump`).
 */
const ToastItem = memo(function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useStore(state => state.dismissToast)
  const [paused, setPaused] = useState(false)
  const remainingRef = useRef(toast.duration)
  const handleDismiss = useCallback(() => dismiss(toast.id), [dismiss, toast.id])
  const Icon = ICONS[toast.severity]

  useEffect(() => { remainingRef.current = toast.duration }, [toast.bump, toast.duration])

  useEffect(() => {
    if (paused) return
    const startedAt = Date.now()
    const timer = setTimeout(handleDismiss, remainingRef.current)
    return () => {
      clearTimeout(timer)
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt))
    }
  }, [handleDismiss, paused, toast.bump])

  const handleFocusItem = useCallback(() => {
    if (toast.focus) useStore.getState().focusAgendaItem(toast.focus)
  }, [toast.focus])

  const pause = useCallback(() => setPaused(true), [])
  const resume = useCallback(() => setPaused(false), [])

  return (
    <div
      role={toast.severity === 'error' ? 'alert' : 'status'}
      className={`tp-toast is-${toast.severity}${paused ? ' is-paused' : ''}`}
      style={{ '--toast-duration': `${toast.duration}ms` } as CSSProperties}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <Icon size={14} aria-hidden="true" className="tp-toast__icon" />
      <span className="tp-toast__message" title={toast.message}>{toast.message}</span>
      {toast.count > 1 && (
        <span className="tp-toast__count" aria-label={`${toast.count} fois`}>×{toast.count}</span>
      )}
      {(toast.focus || (toast.actions && toast.actions.length > 0)) && (
        <span className="tp-toast__actions">
          {toast.focus && (
            <button type="button" onClick={handleFocusItem} className="tp-toast__action">Voir</button>
          )}
          {toast.actions?.map((action, i) => (
            <ToastActionButton key={i} action={action} toastId={toast.id} />
          ))}
        </span>
      )}
      <button type="button" onClick={handleDismiss} title="Fermer" className="tp-toast__close" aria-label="Fermer la notification">
        <X size={13} aria-hidden="true" />
      </button>
      <span key={toast.bump} className="tp-toast__progress" aria-hidden="true" />
    </div>
  )
})

/**
 * Pile bas-gauche : au plus trois cartes (le slice écarte la plus ancienne),
 * hauteur bornée, jamais persistante. Ce qui en sort reste consultable dans la
 * cloche de la barre d'application, rechargement compris.
 */
export const ToastContainer = memo(function ToastContainer() {
  const toasts = useToasts()
  const unread = useUnreadNoticeCount()
  const openCenter = useCallback(() => useStore.getState().setNoticeCenterOpen(true), [])
  if (!toasts.length) return null

  const beyond = unread - toasts.length

  return (
    <div className="tp-toast-stack" aria-live="polite">
      {beyond > 0 && (
        <button type="button" className="tp-toast-more tp-feedback-interactive" onClick={openCenter}>
          <Bell size={12} aria-hidden="true" />
          {beyond === 1 ? '+ 1 autre dans l’historique' : `+ ${beyond} autres dans l’historique`}
        </button>
      )}
      {toasts.map(toast => (
        <div key={toast.id} className="tp-feedback-interactive">
          <ToastItem toast={toast} />
        </div>
      ))}
    </div>
  )
})
